import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  FormInputArtifact,
  FormField,
  CredentialConflictCandidate,
  CredentialConflictsResponse,
} from '@dreamai/types';
import api from '../../../api';
import TransactionalSheet, { SheetPresentation } from './TransactionalSheet';
import { LockIcon } from './transactional-icons';
import {
  DropTaskConfirm,
  SiteGlyph,
  VaultProof,
  VaultSaved,
  displayHost,
} from './CredentialSheetSections';
import {
  SecureCredentialSubmitError,
  SecureCredentialSubmitResult,
  describeSubmitError,
} from './credentialSubmitResult';
import CredentialFieldEditor, {
  CredentialRow,
  collapseRowsForSubmit,
  hasAtLeastOneSecretValue,
  inferIsSecretFromKey,
  makeCredentialRow,
  rowIncompletenessByRowId,
  rowsHaveDuplicateNames,
} from './CredentialFieldEditor';
import { capitalize, rowsFromFormFields } from './credentialRowMapping';
import './secure-credential-modal.scss';

/**
 * Submit options forwarded alongside the credential field values. Used
 * by callers that POST to /memories/store-credential or
 * /response-artifacts/.../submit and need to pass the disambiguator,
 * the explicit secret-key set, or the replace-target along to the BE.
 *
 * Keep additive — every new field is optional so existing callers stay
 * compatible without touching their handlers.
 */
export interface SecureCredentialSubmitOptions {
  /** Free-text disambiguator for users with multiple credentials per site. */
  purpose?: string;
  /**
   * Echo of the update-mode anchor so the BE can deactivate the old row.
   * Mirrors the prop the caller passed in as `existingCredentialId`.
   */
  replacesCredentialId?: string;
  /**
   * Explicit list of field keys whose values are secrets. Forwarded to the
   * BE secret-count gate so user-chosen field names (e.g. "magic_word")
   * are honoured rather than rejected by the key-name heuristic.
   */
  secretFieldKeys?: string[];
  /**
   * User-chosen label, when the modal exposed the Label input itself
   * (i.e. the caller didn't pass a fixed `label` prop). The caller forwards
   * this to the BE in place of any auto-derived label.
   */
  label?: string;
  /**
   * User-chosen site, when the modal exposed the Site input itself
   * (the user-initiated Memories Add path).
   */
  site?: string;
}

interface SecureCredentialModalProps {
  /**
   * Optional response-artifact source. When supplied, the modal pre-seeds
   * rows from `artifact.fields`, locks their names, and resolves
   * label/site/overview/purpose/existingCredentialId from the artifact
   * unless explicit props override them.
   */
  artifact?: FormInputArtifact;
  /**
   * Modal title rendered above the body. Falls back to the artifact's
   * title and then to a sensible default.
   */
  title?: string;
  /**
   * When supplied, the credential's `site` is fixed (renders as a badge,
   * not an editable input). When omitted AND the modal isn't in artifact
   * mode, the user can type a site themselves.
   */
  site?: string;
  /**
   * Optional fixed label. When supplied, the modal does NOT render the
   * Label input — the credential's row-level label is decided by the
   * caller (e.g. the artifact title, or the agent's clarifying question).
   * When omitted (user-initiated Memories Add), the Label input shows
   * up so the user can name the credential themselves.
   */
  label?: string;
  /**
   * Body copy rendered above the editor. Same precedence as `title`.
   */
  overview?: string;
  /**
   * @deprecated Use `initialRows` for new code. Legacy callers pass a
   * `FormField[]` and the modal converts each entry to a locked editor
   * row (matching the old rigid-field behaviour). Prefer `initialRows`
   * so the caller controls lock state and helper visibility directly.
   */
  formFields?: FormField[];
  /**
   * Pre-seeded editor rows. When neither this nor `formFields` nor an
   * artifact is supplied, the editor defaults to a `[username, password]`
   * pair (the common-case user-initiated shape).
   */
  initialRows?: CredentialRow[];
  /**
   * Names that can't be renamed or removed in the editor. Defaults to the
   * names derived from `artifact.fields` or `formFields` (agent-supplied
   * shapes are locked); user-initiated paths leave this empty so every
   * row can be edited freely.
   */
  lockedKeys?: string[];
  submitLabel?: string;
  /**
   * Mark the modal as REPLACING an existing saved credential. Shown as
   * an update banner; on submit the id is echoed back to the caller as
   * `opts.replacesCredentialId` so it can hit the right BE flag.
   */
  existingCredentialId?: string;
  /**
   * Whether the user can add rows beyond the initial seed. Defaults to
   * true everywhere — the data model is "arbitrary fields, at least one
   * secret" so the user should always be free to extend a credential.
   */
  allowAdd?: boolean;
  /**
   * Helper buttons to expose on rows. Only the ops worker form passes
   * `useUserEmail`; password generation and copy default to off.
   */
  helpers?: {
    generatePassword?: boolean;
    useUserEmail?: () => Promise<string | null>;
    copyValue?: boolean;
  };
  /**
   * Initial value for the in-modal Label input. Used by edit-existing
   * callers (CredentialCard) to pre-fill the input with the current row
   * label so the user can rename inline.
   */
  initialLabel?: string;
  /**
   * Initial value for the in-modal Site input. Same role as initialLabel
   * — pre-fills the input when entering update mode so the user sees
   * what's currently stored.
   */
  initialSite?: string;
  /**
   * Perform the save. The modal stays mounted afterwards: on `{ ok: true }`
   * it shows the in-sheet success screen and calls `onClose` when the user
   * dismisses it; on `{ ok: false }` it renders the failure inline and
   * keeps the form editable. Callers must not unmount the modal from
   * inside `onSubmit` — only from `onClose`.
   */
  onSubmit: (
    values: Record<string, string>,
    opts: SecureCredentialSubmitOptions,
  ) => Promise<SecureCredentialSubmitResult>;
  /**
   * "I can’t provide this" — drops the task. The modal asks for
   * confirmation first; this only fires once the user confirms.
   */
  onAcknowledge?: () => void;
  /**
   * Dismiss / finish. Always used by the success screen's Done button;
   * only wired to a close control, Escape and the scrim when
   * `dismissible` is true.
   */
  onClose: () => void;
  /** `modal` (default) layers over the app; `standalone` is the page. */
  presentation?: SheetPresentation;
  /**
   * Whether the sheet can be dismissed without acting (close control,
   * Escape, scrim). Defaults to true for modals. Standalone pages are
   * never dismissible — there is nothing behind them.
   */
  dismissible?: boolean;
  submitting: boolean;
  readOnly?: boolean;
  /** Assistant nickname for the success / drop copy. */
  agentName?: string;
  /**
   * Names the surrounding task in the drop-confirm copy, as a noun phrase
   * that reads after "stops work on" ("the WWE renewal"). Defaults to
   * "this task".
   */
  taskTitle?: string;
}

const USERNAME_PASSWORD_NAMES = new Set(['username', 'password', 'email']);

const DEFAULT_INITIAL_ROWS = (): CredentialRow[] => [
  makeCredentialRow({
    name: 'Username',
    value: '',
    isSecret: false,
  }),
  makeCredentialRow({
    name: 'Password',
    value: '',
    isSecret: true,
  }),
];

const SecureCredentialModal: React.FC<SecureCredentialModalProps> = ({
  artifact,
  title: titleProp,
  site: siteProp,
  label: labelProp,
  overview: overviewProp,
  formFields,
  initialRows,
  lockedKeys: lockedKeysProp,
  submitLabel: submitLabelProp,
  existingCredentialId: existingCredentialIdProp,
  allowAdd = true,
  helpers,
  initialLabel,
  initialSite,
  onSubmit,
  onAcknowledge,
  onClose,
  presentation = 'modal',
  dismissible = true,
  submitting,
  readOnly,
  agentName,
  taskTitle: taskTitleProp,
}) => {
  const canDismiss = presentation === 'modal' && dismissible;
  // ─── Resolve top-level metadata ────────────────────────────────────────
  const artifactTitle = titleProp || artifact?.title || undefined;
  const fixedSite = siteProp || artifact?.credentialSite || undefined;
  const fixedLabel = labelProp || artifact?.title || undefined;
  const overview = overviewProp || artifact?.overview;
  const existingCredentialId =
    existingCredentialIdProp || artifact?.existingCredentialId;
  const isUpdate = !!existingCredentialId;
  const submitLabel =
    submitLabelProp ||
    artifact?.submitLabel ||
    (isUpdate ? 'Update in Bitwarden' : 'Save to Bitwarden');
  // Task context: the sign-in unblocks a job an assistant is doing right
  // now (artifact / escalation / add-todo). Memories add + edit are not.
  const inTask = !!artifact || !!onAcknowledge || !!fixedLabel;
  const taskTitle = taskTitleProp;
  const assistant = agentName || 'your secretary';
  const Assistant = agentName || 'Your secretary';

  // Show the label/site inputs:
  //  - when the caller didn't fix them (user-initiated paths)
  //  - OR when we're in update mode (the user is editing the row, so
  //    even an agent-supplied label should be renameable; same for site)
  // TODO: re-enable once label UX is clearer for users
  const showLabelInput = false; // !fixedLabel || isUpdate;
  const showSiteInput = !fixedSite || isUpdate;

  // ─── Editor rows ────────────────────────────────────────────────────────
  // Resolve initial rows in order of precedence: explicit `initialRows`,
  // legacy `formFields`, artifact's `fields`, then the default seed.
  // We also track whether the caller hand-supplied a row shape so the
  // auto-reveal effect below knows whether it should fetch the live
  // credential fields (update-mode callers like the "Yes, change" flow
  // pass `existingCredentialId` but no rows — without an auto-fetch the
  // editor would render blank `username`/`password` rows instead of the
  // user's actual stored values).
  // Memoise on mount only — same reasoning as initialRowsResolved
  // (we don't want a new prop reference to flip this back to false
  // mid-edit and trigger a re-fetch that clobbers the user's typing).
  const callerProvidedRows = useMemo(
    () =>
      (initialRows && initialRows.length > 0) ||
      (formFields && formFields.length > 0) ||
      !!(artifact?.fields && artifact.fields.length > 0),
    [],
  );
  // Seeds are deliberately memoised on mount only — we don't want the
  // user's typing to be reset if the caller hands back a new formFields
  // array reference on every render.
  const initialRowsResolved: CredentialRow[] = useMemo(() => {
    if (initialRows && initialRows.length > 0) return initialRows;
    if (formFields && formFields.length > 0)
      return rowsFromFormFields(formFields);
    if (artifact?.fields && artifact.fields.length > 0)
      return rowsFromFormFields(artifact.fields);
    return DEFAULT_INITIAL_ROWS();
  }, []);

  const [rows, setRows] = useState<CredentialRow[]>(initialRowsResolved);

  // Locked keys default to the agent-supplied shape (artifact / formFields)
  // unless the caller overrides. The default rows (Username, Password) are
  // also locked so the user can only fill values, not rename them.
  const lockedKeys: string[] = useMemo(() => {
    if (lockedKeysProp) return lockedKeysProp;
    if (formFields && formFields.length > 0)
      return formFields.map((f) => capitalize(f.fieldId));
    if (artifact?.fields && artifact.fields.length > 0)
      return artifact.fields.map((f) => capitalize(f.fieldId));
    return ['Username', 'Password'];
  }, [lockedKeysProp, formFields, artifact?.fields]);

  // ─── Label / Site / Purpose top-level inputs ───────────────────────────
  // In update mode we pre-fill from `initialLabel`/`initialSite` (the row's
  // current values) so the user sees what's stored and can change either.
  // If the caller didn't pass an explicit `initialLabel` but we're in update
  // mode, fall back to `labelProp`/`artifact.title` — every update-mode
  // caller passes one of those (it's what the conflict banner / heading
  // shows), and falling back lets the input pre-populate without forcing
  // every caller to wire `initialLabel` separately.
  const [userLabel, setUserLabel] = useState<string>(
    initialLabel ?? (isUpdate ? (fixedLabel ?? '') : ''),
  );
  const [userSite, setUserSite] = useState<string>(
    initialSite || fixedSite || '',
  );

  const { getAccessTokenSilently } = useAuth0();

  // ─── Auto-reveal in update mode ────────────────────────────────────────
  // When a caller passes `existingCredentialId` but no rows seed (the
  // common "Yes, change" / TaskEscalation / AddTodo flow), fetch the
  // current vault fields so the editor shows what's actually stored.
  // Without this the modal would render the default `[username, password]`
  // skeleton even when the saved credential has e.g. `email`, `password`
  // and `notes` rows — confusing the user into thinking their data was
  // lost. Skip the fetch when the caller hand-supplied row data
  // (CredentialCard does this; it has already revealed the fields and
  // wants those exact values pre-populated).
  const [revealing, setRevealing] = useState<boolean>(
    !callerProvidedRows && !!existingCredentialId,
  );
  const [revealError, setRevealError] = useState<string | null>(null);

  // ─── Sheet state: proof disclosure, submit outcome, drop confirm ──────
  const [proofOpen, setProofOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] =
    useState<SecureCredentialSubmitError | null>(null);
  const [confirmingDrop, setConfirmingDrop] = useState(false);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );
  const heroId = useMemo(
    () => `sc-hero-${Math.random().toString(36).slice(2)}`,
    [],
  );

  useEffect(() => {
    if (callerProvidedRows || !existingCredentialId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<{
          success: boolean;
          fields?: Record<string, string>;
          error?: string;
        }>(`/memories/credentials/${existingCredentialId}/reveal`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const fields = response.data?.fields;
        if (!fields) {
          setRevealError(
            response.data?.error || 'Failed to load saved credential',
          );
          setRevealing(false);
          return;
        }
        const seeded = Object.entries(fields).map(([k, v]) =>
          makeCredentialRow({
            name: k,
            value: v,
            // Vaulted fields carry no secret flag (reveal returns a plain
            // Record<string,string>), so re-derive it. Use the broader
            // `inferIsSecretFromKey` — which mirrors the BE
            // `fieldsContainSecret` substrings/suffixes — rather than the
            // narrow exact-name check, so revealed rows like `apiToken` or
            // `accessKey` come back masked and satisfy the submit gate.
            isSecret: inferIsSecretFromKey(k),
          }),
        );
        if (seeded.length > 0) setRows(seeded);
        setRevealing(false);
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to load saved credential';
        setRevealError(message);
        setRevealing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Mount-only on purpose: re-fetching after the user has already
    // started editing would clobber their in-progress changes. The
    // caller can re-mount the modal to force a refresh.
  }, []);

  // ─── Conflict pre-flight ───────────────────────────────────────────────
  // The conflict pre-flight populates `conflicts` with sibling credentials
  // that share the same site. When the list is non-empty we escalate the
  // single `Label` input below — required marker, hint copy shifts, submit
  // gates on it being non-empty. We deliberately do NOT render a second
  // "Label to tell this sign-in apart" field: from the user's perspective
  // those two inputs answer the same question ("what should I call this?")
  // and having two of them on the same form is confusing. The disambiguation
  // case is just the required-variant of the optional case.
  const [conflicts, setConflicts] = useState<CredentialConflictCandidate[]>([]);
  const effectiveSite = (fixedSite || userSite || '').trim();

  useEffect(() => {
    if (isUpdate || !effectiveSite) {
      setConflicts([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<CredentialConflictsResponse>(
          '/memories/credential-conflicts',
          {
            params: { site: effectiveSite, credentialType: 'login' },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!cancelled) setConflicts(response.data?.candidates || []);
      } catch (err) {
        console.warn('[SecureCredentialModal] Conflict pre-flight failed', err);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [effectiveSite, isUpdate, getAccessTokenSilently]);

  // ─── Submit gating ─────────────────────────────────────────────────────
  // Three hard rules:
  //  1. At least one row marked Secret must carry a non-empty value
  //     (mirrors the BE secret-count gate).
  //  2. No duplicate field names (the BE would last-write-wins them, but
  //     it almost always indicates a user mistake).
  //  3. Every row carrying a value must also have a name. Without this,
  //     `collapseRowsForSubmit` silently drops the row on serialisation
  //     and the user thinks the value was saved when it wasn't.
  // The label input is genuinely optional everywhere — the caller / BE
  // derives a label from site + username + email when the user leaves it
  // blank, so we don't gate the submit on it even though we display it
  // prominently.
  const hasSecret = hasAtLeastOneSecretValue(rows);
  const hasDuplicates = rowsHaveDuplicateNames(rows).size > 0;
  // Block submit when any row is incomplete (missing name OR missing
  // value). The user can always remove fields they don't intend to
  // populate via the × button. This stops:
  //   - silent data loss when a value is typed into a nameless row
  //   - empty-value rows cluttering the vault
  //   - half-filled rows from the user clicking "+ Add field" then
  //     forgetting to fill it before saving
  const hasIncompleteRows = rowIncompletenessByRowId(rows).size > 0;
  // Conflicts → label becomes required. We can't auto-derive a unique label
  // for the user without guessing; better to make them pick one.
  const labelRequired = !isUpdate && conflicts.length > 0 && showLabelInput;
  const labelMissing = labelRequired && userLabel.trim().length === 0;
  const submitOk =
    hasSecret &&
    !hasDuplicates &&
    !hasIncompleteRows &&
    !labelMissing &&
    !revealing;

  // ─── Generate-password helper (opt-in via helpers.generatePassword) ────
  const generatePassword = async (): Promise<string | null> => {
    if (!helpers?.generatePassword) return null;
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/memories/generate-password',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data?.password ?? null;
    } catch (err) {
      console.warn('[SecureCredentialModal] Generate password failed', err);
      return null;
    }
  };

  const editorHelpers = useMemo(
    () => ({
      generatePassword: helpers?.generatePassword
        ? generatePassword
        : undefined,
      useUserEmail: helpers?.useUserEmail,
      copyValue: helpers?.copyValue,
    }),
    // generatePassword is recreated each render but the editor only
    // captures it during the click handler — stable identity isn't
    // necessary, so we intentionally omit it from the dep list.
    [helpers?.generatePassword, helpers?.useUserEmail, helpers?.copyValue],
  );

  const isBusy = submitting || busy;

  const handleSubmit = async () => {
    if (!submitOk || isBusy || readOnly) return;
    const { fields, secretFieldKeys } = collapseRowsForSubmit(rows);
    // Label/site forwarding rules:
    //  - Create mode: send only when the user typed something. Empty stays
    //    empty so the caller's fallback derivation kicks in (e.g.
    //    Memories Add derives a label from the site / username).
    //  - Update mode: ALWAYS send the current value (even when empty so
    //    the caller can clear the site column via PATCH). Without this
    //    the user couldn't blank out a previously-set site.
    const labelOpt = showLabelInput
      ? isUpdate
        ? { label: userLabel.trim() }
        : userLabel.trim()
          ? { label: userLabel.trim() }
          : {}
      : {};
    const siteOpt = showSiteInput
      ? isUpdate
        ? { site: userSite.trim() }
        : userSite.trim()
          ? { site: userSite.trim() }
          : {}
      : {};
    setSubmitError(null);
    setBusy(true);
    try {
      const result = await onSubmit(fields, {
        // No separate `purpose` payload — the FE now exposes a single
        // `Label` input that handles both the "what to call this" and
        // "how to disambiguate from siblings" cases. The BE column for
        // `purpose` still exists for legacy data; new entries only set
        // `label`.
        replacesCredentialId: existingCredentialId,
        secretFieldKeys,
        ...labelOpt,
        ...siteOpt,
      });
      if (!mountedRef.current) return;
      if (result.ok) setSaved(true);
      else setSubmitError(result.error);
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const confirmDrop = () => {
    setConfirmingDrop(false);
    onAcknowledge?.();
  };

  // ─── Copy ──────────────────────────────────────────────────────────────
  const siteHost = fixedSite ? displayHost(fixedSite) : '';
  const siteLine = fixedSite ? (
    <span>
      <b>{siteHost || fixedSite}</b>
      {artifactTitle && artifactTitle !== fixedSite ? (
        <> · {artifactTitle}</>
      ) : null}
    </span>
  ) : (
    <span>
      <b>{artifactTitle || (isUpdate ? 'Saved sign-in' : 'New sign-in')}</b>
    </span>
  );
  const vaultedWhat = rows.every((r) =>
    USERNAME_PASSWORD_NAMES.has(r.name.trim().toLowerCase()),
  )
    ? 'Username and password'
    : 'Every field above';
  const errorCopy = submitError
    ? describeSubmitError(submitError, { isUpdate })
    : null;

  const proof = (
    <VaultProof
      open={proofOpen}
      onToggle={() => setProofOpen((v) => !v)}
      vaultedWhat={vaultedWhat}
    />
  );

  const successScreen = (
    <VaultSaved
      isUpdate={isUpdate}
      inTask={inTask}
      assistant={Assistant}
      onDone={onClose}
    />
  );

  const footer = (
    <>
      <button
        className='save tx-btn-primary'
        type='button'
        onClick={handleSubmit}
        disabled={!submitOk || readOnly || isBusy}
      >
        {isBusy ? 'Sending to the vault…' : submitLabel}
      </button>
      {onAcknowledge && (
        <button
          className='drop tx-btn-text'
          type='button'
          onClick={() => setConfirmingDrop(true)}
          disabled={isBusy}
        >
          I can’t provide this
        </button>
      )}
    </>
  );

  const dropConfirm = confirmingDrop ? (
    <DropTaskConfirm
      id={`${heroId}-confirm`}
      assistant={assistant}
      taskTitle={taskTitle}
      onKeep={() => setConfirmingDrop(false)}
      onDrop={confirmDrop}
    />
  ) : undefined;

  return (
    <TransactionalSheet
      show
      presentation={presentation}
      onClose={canDismiss ? onClose : undefined}
      holdOpen={confirmingDrop || isBusy}
      controlsDisabled={isBusy}
      leading={saved ? undefined : <LockIcon />}
      ariaLabelledBy={heroId}
      className='secure-credential-modal'
      footer={saved ? undefined : footer}
      overlay={dropConfirm}
    >
      {saved ? (
        successScreen
      ) : (
        <>
          <div className='tx-head'>
            <h1 className='tx-title hero' id={heroId}>
              {isUpdate ? (
                <>
                  Update what’s in the vault.
                  <em>The old details are replaced.</em>
                </>
              ) : (
                <>
                  Your password goes into a vault.
                  <em>Not into Dream.</em>
                </>
              )}
            </h1>
            <div className='about'>
              <div className='site'>
                <SiteGlyph site={fixedSite} />
                {siteLine}
              </div>
              {overview && <p className='tx-sub brief'>{overview}</p>}
            </div>
          </div>

          {revealError && (
            <div className='notice danger' role='alert'>
              <b>Couldn’t load your saved sign-in.</b>
              <span>
                {revealError}. Re-enter the details below — saving replaces the
                existing entry.
              </span>
            </div>
          )}

          {conflicts.length > 0 && (
            <div className='notice' role='status'>
              <b>
                You already have {conflicts.length} saved sign-in
                {conflicts.length > 1 ? 's' : ''} for this service.
              </b>
              <span>
                {conflicts.map((c) => c.label).join(', ')}. Same account? Edit
                it from Memories instead. Different account? Carry on.
              </span>
            </div>
          )}

          <div className='fields'>
            {showSiteInput && (
              <div className='field'>
                <label htmlFor={`${heroId}-site`}>Website or service</label>
                <div className='input'>
                  <input
                    id={`${heroId}-site`}
                    type='text'
                    placeholder='e.g. ebay.com'
                    value={userSite}
                    onChange={(e) => setUserSite(e.target.value)}
                    disabled={readOnly || isBusy}
                    spellCheck={false}
                    autoCapitalize='none'
                    autoComplete='url'
                  />
                </div>
              </div>
            )}

            {showLabelInput && (
              <div className='field'>
                <label htmlFor={`${heroId}-label`}>
                  {labelRequired
                    ? 'Label — give this sign-in a name to tell it apart'
                    : 'Label (optional)'}
                </label>
                <div className={`input ${labelMissing ? 'input-error' : ''}`}>
                  <input
                    id={`${heroId}-label`}
                    type='text'
                    placeholder={
                      labelRequired
                        ? 'e.g. Personal account, Work expenses'
                        : 'Describe the account'
                    }
                    value={userLabel}
                    onChange={(e) => setUserLabel(e.target.value)}
                    disabled={readOnly || isBusy}
                    maxLength={120}
                  />
                </div>
              </div>
            )}

            {revealing ? (
              <div className='revealing' role='status'>
                <div className='field'>
                  <span className='skeleton skeleton-label' />
                  <span className='skeleton skeleton-input' />
                </div>
                <div className='field'>
                  <span className='skeleton skeleton-label' />
                  <span className='skeleton skeleton-input' />
                </div>
                <p>Loading what’s in the vault…</p>
              </div>
            ) : (
              <CredentialFieldEditor
                rows={rows}
                onChange={setRows}
                lockedKeys={lockedKeys}
                disabled={readOnly || isBusy}
                helpers={editorHelpers}
                allowAdd={allowAdd}
              />
            )}
          </div>

          {errorCopy && (
            <div className='notice danger' role='alert'>
              <b>{errorCopy.title}</b>
              <span>{errorCopy.body}</span>
            </div>
          )}

          {proof}
        </>
      )}
    </TransactionalSheet>
  );
};

export default SecureCredentialModal;
